import React, { useState, useEffect } from 'react';
import { productAPI } from '../../services/api';
import '../Customers/Customers.css';

const ProductForm = ({ productId, onClose, onSuccess }) => {
  const isEditMode = Boolean(productId);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    size: '',
    weight_code: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [updateOrderItems, setUpdateOrderItems] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchProduct();
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getById(productId);
      setFormData({
        code: response.code || '',
        description: response.description || '',
        size: response.size || '',
        weight_code: response.weight_code || '',
      });
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to load product');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Product code is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.size === '' || formData.size < 0 || formData.size > 13) {
      newErrors.size = 'Size must be between 0 and 13';
    }

    if (!formData.weight_code || formData.weight_code < 1) {
      newErrors.weight_code = 'Weight must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        size: parseInt(formData.size, 10),
        weight_code: parseInt(formData.weight_code, 10),
      };

      if (isEditMode) {
        const updateData = updateOrderItems
          ? { ...submitData, update_order_items: true }
          : submitData;
        await productAPI.update(productId, updateData);
      } else {
        await productAPI.create(submitData);
      }

      onSuccess();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      setSubmitError(
        err.response?.data?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} product`
      );
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {submitError && (
              <div className="error-message">{submitError}</div>
            )}

            {loading && isEditMode && !formData.code ? (
              <div className="loading">Loading product...</div>
            ) : (
              <>
                <div className="form-section">
                  <h3>Product Information</h3>

                  <div className="form-group">
                    <label htmlFor="code">
                      Product Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      className={errors.code ? 'error' : ''}
                      placeholder="e.g., PINE"
                      maxLength="50"
                    />
                    {errors.code && (
                      <span className="error-text">{errors.code}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">
                      Description <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className={errors.description ? 'error' : ''}
                      placeholder="e.g., Dried Pine"
                      maxLength="255"
                    />
                    {errors.description && (
                      <span className="error-text">{errors.description}</span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="size">
                        Size <span className="required">*</span> (0-13)
                      </label>
                      <input
                        type="range"
                        id="size"
                        name="size"
                        value={formData.size}
                        onChange={handleChange}
                        className={errors.size ? 'error' : ''}
                        min="0"
                        max="13"
                      />
                      <span className="slider-value">{formData.size}</span>
                      {formData.size == 0 && (
                        <div className="info-text" style={{ marginTop: '10px' }}>
                          Products with a size of 0 are only mentioned in the order notes and are not assignable to loading positions.
                        </div>
                      )}
                      {errors.size && (
                        <span className="error-text">{errors.size}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="weight_code">
                        Weight <span className="required">*</span>
                      </label>
                      <select
                        id="weight_code"
                        name="weight_code"
                        value={formData.weight_code}
                        onChange={handleChange}
                        className={errors.weight_code ? 'error' : ''}
                      >
                        <option value="">Select weight...</option>
                        <option value="1">Light (1)</option>
                        <option value="2">Medium (2)</option>
                        <option value="3">Heavy (3)</option>
                      </select>
                      {errors.weight_code && (
                        <span className="error-text">{errors.weight_code}</span>
                      )}
                    </div>
                  </div>

                  {isEditMode && (
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={updateOrderItems}
                          onChange={(e) => setUpdateOrderItems(e.target.checked)}
                        />
                        <span>Update existing order items with new weight</span>
                      </label>
                      <div className="info-text" style={{ marginTop: '5px' }}>
                        When enabled, this will update the weight on all related order items. Size, quantity, and price will not be affected.
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;